import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Input } from 'antd'
import { debounce } from 'lodash'

const { Search } = Input

// Self-contained search box: owns the input value and debounces changes,
// emitting only the settled term via `onSearch`. Pressing Enter / the search
// button (or clearing) emits immediately, skipping the debounce wait.
const SearchInput = ({ placeholder, onSearch, delay = 400, ...rest }) => {
  const [value, setValue] = useState('')

  // Keep the latest callback in a ref so the debounced fn stays stable even when
  // the parent passes a new `onSearch` on every render.
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  const emit = useMemo(
    () => debounce((v) => onSearchRef.current && onSearchRef.current(v), delay),
    [delay],
  )
  useEffect(() => () => emit.cancel(), [emit])

  const handleChange = (e) => {
    const v = e.target.value
    setValue(v)
    emit(v)
  }

  // Enter / search button: emit now, dropping any pending debounced call.
  const handleSearchNow = (v) => {
    emit.cancel()
    if (onSearchRef.current) onSearchRef.current(v)
  }

  return (
    <Search
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onSearch={handleSearchNow}
      allowClear
      {...rest}
    />
  )
}

export default SearchInput
